import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import BindingMatrix from "components/BindingMatrix/BindingMatrix.jsx";
import BindingEditor from "components/BindingEditor/BindingEditor.jsx";
import BindingConflictPanel from "components/BindingConflictPanel/BindingConflictPanel.jsx";
import {
  createAssetAgent,
  deleteAssetAgent,
  getAssetAgents,
  getAssets
} from "services/assetDataProvider";
import { getUserDevices } from "services/dashboardDataProvider";
import Button from "components/CustomButtons/Button.jsx";

function normalizeDevicesPayload(payload) {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.devices)) {
    return payload.devices;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
}

export default function AssetBindings({ axios }) {
  const [assets, setAssets] = useState([]);
  const [devices, setDevices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    return Promise.all([
      getAssets(axios),
      getAssetAgents(axios),
      getUserDevices(axios, { page_size: 1000 })
    ]).then(([assetsData, agentsData, devicesData]) => {
        setAssets(assetsData || []);
        setAgents(agentsData || []);
        setDevices(normalizeDevicesPayload(devicesData));
      });
  };

  useEffect(() => {
    load();
  }, [axios]);

  const assetById = useMemo(() => {
    const next = {};
    assets.forEach(item => {
      next[item.id] = item;
    });
    return next;
  }, [assets]);

  const onSave = payload => {
    setSaving(true);
    setError("");
    return createAssetAgent(axios, payload)
      .then(() => load())
      .catch(err => {
        setError(err.message || "Failed to create binding agent.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const remove = agentId => {
    if (!window.confirm("Delete this binding agent?")) {
      return;
    }
    deleteAssetAgent(axios, agentId).then(() => load());
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={5}>
        <Card>
          <CardHeader color="info">
            <h4>Create Asset Binding Agent</h4>
          </CardHeader>
          <CardBody>
            <BindingEditor
              assets={assets}
              devices={devices}
              onSave={onSave}
              isSaving={saving}
            />
            {error ? <div style={{ color: "#b91c1c" }}>{error}</div> : null}
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12} sm={12} md={7}>
        <Card>
          <CardHeader color="primary">
            <h4>Conflict Panel</h4>
          </CardHeader>
          <CardBody>
            <BindingConflictPanel agents={agents} />
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="warning">
            <h4>Binding Matrix</h4>
          </CardHeader>
          <CardBody>
            <BindingMatrix agents={agents} assetById={assetById} />
            <div style={{ marginTop: 12 }}>
              {agents.map(agent => (
                <Button
                  key={agent.id}
                  size="sm"
                  color="danger"
                  onClick={() => remove(agent.id)}
                  style={{ marginRight: 8, marginBottom: 8 }}
                >
                  Delete {agent.name}
                </Button>
              ))}
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

AssetBindings.propTypes = {
  axios: PropTypes.object.isRequired
};
